import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCoachCompanies } from '../../../../api/ols/coach-companies/get-coach-companies'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../lib/hooks/use-translate'
import { TransportTypeEnum } from '../../../../lib/types/enums/trip/trip-type.enum'
import {
  TripStepTypeEnum,
  tripStepTypeMapping
} from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripBookingTypes } from '../../../../lib/types/models/trip-booking/trip-booking'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import TransportPicto from '../../../elements/transport-picto'
import LabelWithText from '../../ols/label-with-text'
import { OlCenterTripMode } from './ol-center-tabs'

type Props = {
  olId?: number
  data: TripBookingTypes.View
  mode?: OlCenterTripMode
  onCoachCompanyChange?: (
    coachCompanyId: number,
    directionality: TripStepTypeEnum
  ) => Promise<void>
}

const AssignCoachCompany = ({
  directionality,
  data,
  olId,
  onCoachCompanyChange
}: Props & { directionality: TripStepTypeEnum }) => {
  const [selectedCoachCompany, setSelectedCoachCompany] = useState<number | undefined>(
    data.departureCoachCompany?.id || data.arrivalCoachCompany?.id
  )
  const [loading, setLoading] = useState(false)
  const { t } = useTranslate()
  const { departureCoachCompany, arrivalCoachCompany } = data

  const tripSteps = data.tripSteps.filter((step) => step.directionality === directionality)
  const tripStep = tripSteps[0]
  const coachCompany = departureCoachCompany || arrivalCoachCompany

  const { data: coachCompanies } = useQuery({
    queryKey: ['coach-companies'],
    queryFn: () => {
      if (!olId) return
      return getCoachCompanies({ olId })
    },
    enabled: !!olId
  })

  return (
    <Box className="mt-3 rounded-lg shadow">
      <Typography variant="tag" color={tailwindColors.navyBlue.DEFAULT}>
        {tripStep?.directionality === TripStepTypeEnum.INBOUND
          ? t('ol.center.trip.booking.arrival.day')
          : t('ol.center.trip.booking.departure.day')}{' '}
        :
      </Typography>
      <div className="h-3" />

      {!coachCompanies?.data.length && !coachCompany && (
        <>
          <Typography variant="small-semibold" color={tailwindColors.red.DEFAULT}>
            {t('ol.center.trip.booking.no.coach.company.available')}
          </Typography>
          <div className="h-2" />
        </>
      )}

      {!olId ? (
        <div className="grid grid-cols-6 gap-3">
          <LabelWithText
            label={t('ol.center.trip.booking.coach.company.name')}
            data={valueRender(coachCompany?.name)}
          />

          <LabelWithText
            label={t('ol.center.trip.booking.coach.company.phone.number')}
            data={valueRender(coachCompany?.phoneNumber, (nb) => formatPhoneNumber(nb))}
          />

          <LabelWithText
            label={t('ol.center.trip.booking.coach.company.emergency.number')}
            data={valueRender(coachCompany?.emergencyNumber, (nb) => formatPhoneNumber(nb))}
          />
        </div>
      ) : (
        <>
          <SelectInput
            label={t('ol.center.trip.booking.coach.company.name')}
            options={coachCompanies?.data.map((company) => ({
              label: company.name,
              value: company.id
            }))}
            selectedOption={
              selectedCoachCompany
                ? {
                    label:
                      coachCompanies?.data.find((c) => c.id === selectedCoachCompany)?.name || '',
                    value: selectedCoachCompany
                  }
                : undefined
            }
            onChange={(value) => setSelectedCoachCompany(value)}
          />
          <div className="h-3" />
          <div className="flex justify-end gap-2">
            <Button
              label={t('button.validate')}
              variant="ghost"
              loading={loading}
              disabled={!selectedCoachCompany || loading}
              onClick={() => {
                if (onCoachCompanyChange && selectedCoachCompany) {
                  setLoading(true)
                  onCoachCompanyChange(selectedCoachCompany, directionality)
                    .then(() => {
                      setLoading(false)
                    })
                    .catch(() => {
                      setLoading(false)
                    })
                }
              }}
            />
          </div>
        </>
      )}
    </Box>
  )
}

const OlCenterTripBookingDetails = (props: Props) => {
  const nativeCountryName = useNativeCountryName()
  const {
    data: {
      directionality,
      tripSteps,
      travelCompany,
      departureCoachCompany,
      arrivalCoachCompany,
      transportMeans
    },
    mode = 'default'
  } = props

  const { t } = useTranslate()
  const tripStep = tripSteps[0]
  const coachCompany = departureCoachCompany || arrivalCoachCompany
  const dropOffTime =
    tripStep.directionality === TripStepTypeEnum.OUTBOUND
      ? tripStep.timeBeforeDepartureForGroup
      : tripStep.foreignProviderAppointmentTime

  const getTripText = () => {
    const prefix = mode === 'default' ? 'Voyage ' : ''
    const suffix = mode === 'personal-space' ? ' Trip' : ''
    return `${prefix}${t(tripStepTypeMapping[tripStep.directionality].label)}${suffix}`
  }

  return (
    <Box className="bg-white-hover border border-grey-light">
      <Badge text={getTripText()} variant={tripStepTypeMapping[tripStep.directionality].variant} />
      {!!props.olId && tripStep.directionality === TripStepTypeEnum.OUTBOUND && (
        <AssignCoachCompany directionality={TripStepTypeEnum.OUTBOUND} {...props} />
      )}
      <div className="h-3" />

      <div className="grid grid-cols-8 gap-3">
        <LabelWithText
          label={t('ol.center.trip.booking.details.departure.date')}
          data={<Badge text={DateFormatter.DDMMYYYY(tripStep.departureDate)} />}
        />
        <LabelWithText
          className="col-span-2"
          label={t('ol.center.trip.booking.details.departure.location')}
          data={tripStep.departurePlace.label}
        />
        <LabelWithText
          className="col-span-2"
          label={t('ol.center.trip.booking.details.departure.time')}
          data={<Badge text={DateFormatter.getFormattedTime(tripStep.departureTime)} />}
        />
        <LabelWithText
          label={t('ol.center.trip.booking.details.transport.company')}
          data={valueRender(travelCompany?.name)}
        />
        <LabelWithText
          label={t('ol.center.trip.booking.details.transport')}
          data={transportMeans.picto ? <TransportPicto picto={transportMeans.picto} /> : '-'}
        />
        <LabelWithText
          label={t('ol.center.trip.booking.details.flight.train.number')}
          data={tripStep.transportMeanNumber}
        />
        <LabelWithText
          label={t('ol.center.trip.booking.details.arrival.date')}
          data={<Badge text={DateFormatter.DDMMYYYY(tripStep.arrivalDate)} />}
        />
        <LabelWithText
          className="col-span-2"
          label={t('ol.center.trip.booking.details.arrival.location')}
          data={`${tripStep.arrivalPlace?.label}`}
        />
        <LabelWithText
          className="col-span-5"
          label={t('ol.center.trip.booking.details.arrival.time')}
          data={<Badge text={DateFormatter.getFormattedTime(tripStep.arrivalTime)} />}
        />
      </div>
      <div className="h-3" />
      <Box
        className={cn('bg-navyBlue-light grid grid-cols-8 px-0 py-3 gap-3 rounded', {
          'bg-transparent p-0': mode === 'default'
        })}
      >
        {mode === 'personal-space' && <div />}
        <LabelWithText
          className="col-span-3"
          label={
            directionality === TransportTypeEnum.ONE_WAY
              ? t('ol.center.trip.booking.details.meeting.place.for.company')
              : t('ol.center.trip.booking.details.drop.off.point.for.company')
          }
          data={
            tripStep.foreignProviderAppointmentPlace ? (
              <AddressLink
                name={tripStep.foreignProviderAppointmentPlace.label}
                address1={tripStep.foreignProviderAppointmentPlace.address1}
                address2={tripStep.foreignProviderAppointmentPlace.address2}
                city={tripStep.foreignProviderAppointmentPlace.city}
                country={nativeCountryName(tripStep.foreignProviderAppointmentPlace.country)}
                zipCode={tripStep.foreignProviderAppointmentPlace.zipCode}
              />
            ) : (
              <Badge text="-" />
            )
          }
        />
        {directionality === TransportTypeEnum.ONE_WAY ? (
          <LabelWithText
            className={'col-span-4'}
            label={t('ol.center.trip.booking.details.meeting.time.for.company')}
            data={
              <Badge
                text={valueRender(tripStep.foreignProviderAppointmentTime, (time) =>
                  DateFormatter.getFormattedTime(time)
                )}
              />
            }
          />
        ) : (
          <LabelWithText
            className={'col-span-4'}
            label={t('ol.center.trip.booking.details.drop.off.time.for.company')}
            data={
              <Badge
                text={valueRender(dropOffTime, (time) => DateFormatter.getFormattedTime(time))}
              />
            }
          />
        )}
        {mode === 'personal-space' && <div />}

        <LabelWithText
          className={'col-span-6'}
          label={
            tripStep.directionality === TripStepTypeEnum.INBOUND
              ? t('ol.center.trip.booking.aller.details.instructions.drop.off.for.company')
              : t('ol.center.trip.booking.retour.details.instructions.drop.off.for.company')
          }
          data={tripStep.tripProviderGuidelineContent}
        />
      </Box>

      {!props.olId ? (
        <>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT} className={'mt-5'}>
            {t('ol.center.trip.booking.local.coach.company')}
          </Typography>
          <div className={'h-2'} />
          <div className={'grid grid-cols-7 gap-4'}>
            <LabelWithText
              label={t('ol.center.trip.booking.coach.company.name')}
              data={valueRender(coachCompany?.name)}
            />
            <LabelWithText
              className={'col-span-2'}
              label={t('ol.center.trip.booking.coach.company.phone.number')}
              data={valueRender(coachCompany?.phoneNumber, (phoneNumber) =>
                formatPhoneNumber(phoneNumber)
              )}
            />
            <LabelWithText
              label={t('ol.center.trip.booking.coach.company.emergency.number')}
              data={valueRender(coachCompany?.emergencyNumber, (phoneNumber) =>
                formatPhoneNumber(phoneNumber)
              )}
            />
          </div>
        </>
      ) : (
        tripStep.directionality === TripStepTypeEnum.INBOUND && (
          <AssignCoachCompany directionality={TripStepTypeEnum.INBOUND} {...props} />
        )
      )}
    </Box>
  )
}

export default OlCenterTripBookingDetails
